/**
 * Where to send the user after login: a path on this site, never another host.
 *
 * Mirrors core/lib/Http/ReturnPath.php: only paths that start with a single "/" (not "//" or
 * "/\", which browsers treat as another host), without control characters or backslashes, and
 * not the login or logout pages (so the login cannot loop).
 */

export const RETURN_PATH_MAX_LENGTH = 2048;

const REFUSED = ['/login', '/logout'];

/** The path when it is safe to return to, null otherwise */
export function sanitizeReturnPath(value: unknown): string | null {
  if (typeof value !== 'string' || value === '' || value.length > RETURN_PATH_MAX_LENGTH) {
    return null;
  }
  if (!value.startsWith('/') || value.startsWith('//') || value.includes('\\')) {
    return null;
  }
  // eslint-disable-next-line no-control-regex
  if (/[\x00-\x1F\x7F]/.test(value)) {
    return null;
  }

  const path = value.split(/[?#]/, 1)[0];
  if (REFUSED.some(refused => path === refused || path.startsWith(refused + '/'))) {
    return null;
  }

  return value;
}

/** The login page URL that returns to path afterwards (plain /login when path is not safe or is /) */
export function loginUrl(path: unknown): string {
  const safe = sanitizeReturnPath(path);
  return safe === null || safe === '/' ? '/login' : '/login?return=' + encodeURIComponent(safe);
}

/** The current page as a return path (path and query, without the hash) */
export function currentPath(): string {
  return window.location.pathname + window.location.search;
}
